/**
 * JAPAP Messenger — Call Provider (Sprint B + C — LiveKit)
 * =========================================================
 * Audio/Video 1-1 + Group calls. LiveKit for media, Socket.io for ringing.
 *
 *   Sprint B  ← audio 1-1 ringing flow (caller ↔ callee)
 *   Sprint C  ← video publish/subscribe, group calls (join-live via banner),
 *               active speaker highlight, camera switch
 *   Sprint D  ← record start/stop + AI summary modal (host-only)
 *
 * Video grid participants shape :
 *   [{ sid, identity, name, avatar, videoOn, muted, isSpeaking, isLocal }]
 *
 * Mobile-first: VideoCallGrid uses 1 col <480px / 2 col 480-960 / auto-fit desktop.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
// iter251a — LiveKit replaced by Agora. We keep the import alive only for
// the no-op stubs that telemetry references, otherwise tree-shaking would
// break.
import { useAuth } from '@/context/AuthContext';
import { useRealtime } from '@/context/RealtimeContext';
import IncomingCallOverlay from '@/components/calls/IncomingCallOverlay';
import InCallScreen from '@/components/calls/InCallScreen';
import VideoCallGrid from '@/components/calls/VideoCallGrid';
import { CallReturnPill } from '@/components/calls/CallControls';
import CallSummaryModal from '@/components/calls/CallSummaryModal';
import { useTranslation } from 'react-i18next';
// iter249c — Restore the missing `callLog` import.
import { callLog } from '@/utils/callTelemetry';
import { createAgoraSession, playRemoteVideo } from '@/agora/client';
import GroupCallSeats from '@/components/calls/GroupCallSeats';
// iter252a — Modern WhatsApp/iPhone-style ringtones (Web Audio synth).
import { createOutgoingRingback, createIncomingRingtone } from '@/utils/callRingtones';

// WebSocket connected, or HTTP fallback installed on the shared socket (RealtimeContext).
const canSignal = (ref) => !!ref.current && (ref.current.connected || ref.current.__convs !== undefined);

const API = process.env.REACT_APP_BACKEND_URL;

const CallContext = createContext(null);

export function CallProvider({ children }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { socket: sharedSocket } = useRealtime();
  const [state, setState] = useState('idle');
  const [callMinimized, setCallMinimized] = useState(false);
  useEffect(() => { if (state === 'idle' || state === 'ringing') setCallMinimized(false); }, [state]);              // idle | calling | ringing | in-call
  const callStateRef = useRef('idle');
  callStateRef.current = state;
  const [callInfo, setCallInfo] = useState(null);
  const [muted, setMuted] = useState(false);
  const [canPublish, setCanPublish] = useState(true);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [videoOn, setVideoOn] = useState(false);           // Sprint C — local camera state
  const [connectionStatus, setConnectionStatus] = useState('connecting'); // connecting | connected | reconnecting | failed
  // iter249b — Track the signaling Socket.IO readiness independently from
  // the LiveKit room state. Used to disable call buttons when ringing
  // can't fan out yet (caller would otherwise click into a void).
  const [socketReady, setSocketReady] = useState(false);
  // iter249b-hotfix — Persistent red banner message shown when a call
  // can't start (socket KO, permission denied, no device, callee offline).
  // Replaces the previously-silent toast that disappeared too fast on
  // mobile PWA behind the OS chrome.
  const [callBlockedMessage, setCallBlockedMessage] = useState(null);
  const [callStartedAt, setCallStartedAt] = useState(null);
  const [participants, setParticipants] = useState([]);    // Sprint C — video grid state
  const [liveGroupCalls, setLiveGroupCalls] = useState({}); // { conv_id: { session_id, mode, host_name, title } }

  const socketRef = useRef(null);
  const roomRef = useRef(null);
  const applyRoleRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const ringtoneRef = useRef(null);
  const callInfoRef = useRef(null);
  const videoRefsRef = useRef({});   // { sid: <div host> } updated via ref callback in VideoCallGrid
  // iter252a — Tracks may arrive (Agora user-published) BEFORE the React
  // tile DOM exists. We buffer remote videoTracks here and replay them
  // when the <div> ref mounts.
  const pendingVideoTracksRef = useRef({});  // { sid: videoTrack }
  const facingModeRef = useRef('user');
  const ringTimeoutRef = useRef(null);   // iter193 — 45s ring timeout
  const [recording, setRecording] = useState(false);        // Sprint D
  const [recordingId, setRecordingId] = useState(null);
  const [endedSessionId, setEndedSessionId] = useState(null); // for auto CallSummaryModal

  useEffect(() => { callInfoRef.current = callInfo; }, [callInfo]);

  // ───── Ringtones (iter252a — modern WhatsApp/iPhone-style via Web Audio)
  // Two distinct sounds:
  //   • 'calling'   (outgoing) → soft two-tone ringback
  //   • 'ringing'   (incoming) → marimba melody arpeggio
  // No external audio files; everything is synthesized.
  useEffect(() => {
    let activeRinger = null;
    if (state === 'calling') {
      activeRinger = createOutgoingRingback();
      activeRinger.play();
    } else if (state === 'ringing') {
      activeRinger = createIncomingRingtone();
      activeRinger.play();
    }
    ringtoneRef.current = activeRinger;
    return () => {
      // Stop on state transition or unmount — crucial: closes the
      // AudioContext so the ringtone cuts INSTANTLY on accept/hangup.
      try { activeRinger?.stop(); } catch { /* no-op */ }
      ringtoneRef.current = null;
    };
  }, [state]);

  // Sprint D — Record toggle (host-only enforced server-side)
  const toggleRecord = useCallback(async () => {
    const info = callInfoRef.current;
    if (!info?.session_id) return;
    try {
      if (!recording) {
        const { data } = await axios.post(`${API}/api/calls/${info.session_id}/record/start`, {}, { withCredentials: true });
        setRecordingId(data.recording_id);
        setRecording(true);
        toast.success('Enregistrement démarré');
      } else {
        await axios.post(`${API}/api/calls/${info.session_id}/record/stop`, {}, { withCredentials: true });
        setRecording(false);
        toast.info('Enregistrement arrêté — la synthèse IA sera prête dans 1-2 min.');
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 503) toast.info(detail || 'Enregistrement non disponible (LiveKit non configuré).');
      else toast.error(detail || 'Erreur d\'enregistrement');
    }
  }, [recording]);

  // ───── Cleanup ─────
  const cleanup = useCallback(() => {
    if (ringTimeoutRef.current) {
      clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = null;
    }
    if (roomRef.current) {
      // iter251a — Agora session has a `leave()` method (fully tears down
      // tracks + client). The old LiveKit `disconnect()` is kept as a
      // fallback for any in-flight Room object if hot-reload kicks in.
      try {
        if (typeof roomRef.current.leave === 'function') {
          // Don't await: cleanup must be synchronous for state ordering.
          roomRef.current.leave().catch(() => {});
        } else if (typeof roomRef.current.disconnect === 'function') {
          roomRef.current.disconnect();
        }
      } catch { /* no-op */ }
      roomRef.current = null;
    }
    setMuted(false);
    setSpeakerOn(true);
    setVideoOn(false);
    setConnectionStatus('connecting');
    setCallStartedAt(null);
    setRecording(false);
    setRecordingId(null);
    setParticipants([]);
    videoRefsRef.current = {};
    facingModeRef.current = 'user';
  }, []);

  // iter254b — Continuous vibration loop for backgrounded callees. Vibrates
  // 1s on / 0.5s off in a 3.5s cycle (each pattern call is auto-cancelled
  // by the next), with a hard 45s timeout matching the OneSignal push TTL.
  const vibrateIntervalRef = useRef(null);
  const vibrateTimeoutRef = useRef(null);
  const startVibrateLoop = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.vibrate) return;
    try { navigator.vibrate([1000, 500, 1000, 500, 1000]); } catch { /* no-op */ }
    if (vibrateIntervalRef.current) clearInterval(vibrateIntervalRef.current);
    vibrateIntervalRef.current = setInterval(() => {
      try { navigator.vibrate([1000, 500, 1000]); } catch { /* no-op */ }
    }, 3500);
    if (vibrateTimeoutRef.current) clearTimeout(vibrateTimeoutRef.current);
    vibrateTimeoutRef.current = setTimeout(() => stopVibrateLoop(), 45_000);
  }, []);
  const stopVibrateLoop = useCallback(() => {
    if (vibrateIntervalRef.current) {
      clearInterval(vibrateIntervalRef.current);
      vibrateIntervalRef.current = null;
    }
    if (vibrateTimeoutRef.current) {
      clearTimeout(vibrateTimeoutRef.current);
      vibrateTimeoutRef.current = null;
    }
    try { navigator.vibrate?.(0); } catch { /* no-op */ }
  }, []);



  const endCall = useCallback(async (reason = 'ended') => {
    const info = callInfoRef.current;
    const hadRecording = recordingId && info?.session_id;
    if (info?.call_id) {
      callLog('call_ended', {
        call_id: info.call_id, room_id: info.room_name, meta: { reason },
      });
    }
    // iter254b — Always stop the incoming ringing vibration on hangup.
    stopVibrateLoop();
    // iter252a — Optimistic UI: tear down the UI FIRST so the user gets
    // instant feedback (WhatsApp-grade). Network side-effects (leave API,
    // socket emit) are fire-and-forget afterwards. Also fire a short
    // haptic burst on mobile for a tactile "hang up" feel.
    try { navigator.vibrate?.(50); } catch { /* no-op */ }
    if (hadRecording) setEndedSessionId({ session_id: info?.session_id, conv_id: info?.conv_id || null });
    cleanup();
    setState('idle');
    setCallInfo(null);
    // Network side-effects (background, don't block UI)
    if (info?.session_id) {
      axios.post(`${API}/api/calls/${info.session_id}/leave`, {},
        { withCredentials: true }).catch(() => { /* best-effort */ });
    }
    if (info?.call_id && canSignal(socketRef)) {
      // History outcome (backend): unanswered outgoing call = 'missed' on ring timeout, else 'cancelled'.
      const unanswered = callStateRef.current === 'calling';
      try { socketRef.current.emit('call_end', { call_id: info.call_id, peer_id: info.peer_id, answered: !unanswered, reason: unanswered ? (reason === 'missed' ? 'missed' : 'cancelled') : undefined }); }
      catch { /* no-op */ }
    }
    if (info?.kind === 'group' && socketRef.current?.connected) {
      try { socketRef.current.emit('group_call_leave', { conv_id: info.conv_id, session_id: info.session_id }); }
      catch { /* no-op */ }
    }
  }, [cleanup, recordingId]);

  // ───── Participant state helpers (Sprint C) ─────
  const upsertParticipant = useCallback((patch) => {
    setParticipants((prev) => {
      const idx = prev.findIndex((p) => p.sid === patch.sid);
      if (idx === -1) return [...prev, { videoOn: false, muted: false, isSpeaking: false, ...patch }];
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }, []);

  const removeParticipant = useCallback((sid) => {
    setParticipants((prev) => prev.filter((p) => p.sid !== sid));
    delete videoRefsRef.current[sid];
  }, []);

  // ───── Agora session setup (iter251a — replaces LiveKit Room) ─────
  // Naming kept ("connectToRoom") so the rest of the file is untouched.
  // `roomRef` now holds the Agora session handle returned by createAgoraSession.
  const connectToRoom = useCallback(async (sessionId, { publishVideo = false, group = false } = {}) => {
    callLog('token_requested', { call_id: sessionId });
    const { data: tok } = await axios.post(
      `${API}/api/calls/token`,
      { session_id: sessionId },
      { withCredentials: true },
    );
    callLog('agora_connecting', {
      call_id: sessionId, room_id: tok.channel,
      meta: { provider: tok.provider || 'agora', uid: tok.uid, publishVideo },
    });

    // iter252a — Wire the bidirectional contract between VideoCallGrid
    // and Agora: when the grid mounts a tile DIV, replay any buffered
    // remote videoTrack into it; when Agora subscribes a track and the
    // div doesn't exist yet, buffer the track for later mount.
    videoRefsRef.current.__onMount = (sid, el) => {
      const pending = pendingVideoTracksRef.current[sid];
      if (pending && el) {
        try { pending.play(el, { fit: 'cover' }); } catch (e) { /* no-op */ }
        delete pendingVideoTracksRef.current[sid];
      }
    };

    const displayName = (uid) => `Utilisateur ${String(uid).slice(-4)}`;

    // Helper to know if a remote uid is already rendered (de-dupe).
    const ensureRemoteRendered = (user, mediaType) => {
      const sid = String(user.uid);
      if (mediaType === 'video') {
        upsertParticipant({
          sid, identity: sid, name: displayName(user.uid),
          videoOn: true, muted: false, isLocal: false,
        });
        const el = videoRefsRef.current[sid];
        if (el && user.videoTrack) {
          try { playRemoteVideo(user.videoTrack, el); } catch { /* no-op */ }
        } else if (user.videoTrack) {
          // iter252a — Buffer the track until the React tile mounts.
          pendingVideoTracksRef.current[sid] = user.videoTrack;
        }
      } else if (mediaType === 'audio') {
        upsertParticipant({
          sid, identity: sid, name: displayName(user.uid),
          muted: false, isLocal: false,
        });
      }
    };

    // Group calls: the server decides who may speak (tok.can_publish); the web mirrors it like the mobile app.
    const canPub = tok.can_publish !== false;
    setCanPublish(canPub);
    if (!canPub) setMuted(true);
    publishVideo = publishVideo && canPub;
    let session;
    try {
      session = await createAgoraSession({
        appId: tok.app_id,
        channel: tok.channel,
        token: tok.token,
        uid: tok.uid,
        publishVideo,
        live: group,
        canPublish: canPub,
        callbacks: {
          onRemoteUserPublished: (user, mediaType) => {
            ensureRemoteRendered(user, mediaType);
            // iter252a — Real call timer: only start when a remote is actually
            // present. The previous behaviour started it the moment WE joined,
            // showing "00:42" before the callee had even picked up.
            // ANO-001: the event itself proves a remote is present. It can fire
            // before `session` is assigned (remote already publishing at join).
            if (user) setCallStartedAt((prev) => prev || Date.now());
          },
          onRemoteUserUnpublished: (user, mediaType) => {
            if (mediaType === 'video') {
              upsertParticipant({ sid: String(user.uid), videoOn: false });
            }
          },
          onRemoteUserLeft: (user) => {
            removeParticipant(String(user.uid));
          },
          onConnectionStateChange: (st, reason) => {
            // Agora states: DISCONNECTED, CONNECTING, RECONNECTING, CONNECTED, DISCONNECTING.
            if (st === 'CONNECTED') setConnectionStatus('connected');
            else if (st === 'RECONNECTING') setConnectionStatus('reconnecting');
            else if (st === 'DISCONNECTED' && reason && reason !== 'LEAVE') {
              setConnectionStatus('failed');
              toast.error("Connexion à l'appel perdue.");
            }
          },
          onError: (err) => {
            callLog('agora_error', { call_id: sessionId, error: err });
          },
          // Renewal re-reads the seat from the server: a withdrawn speaker goes back to listener here at the latest.
          onTokenWillExpire: async () => {
            try {
              const { data: nt } = await axios.post(`${API}/api/calls/token`, { session_id: sessionId }, { withCredentials: true });
              const s = roomRef.current;
              if (!s) return;
              if (group && nt.can_publish !== s.publishing) await applyRoleRef.current?.();
              else await s.renew(nt.token, nt.can_publish);
            } catch (e) { callLog('agora_error', { call_id: sessionId, error: e }); }
          },
        },
      });
    } catch (e) {
      callLog('media_device_error', {
        call_id: sessionId, error: e, meta: { kind: 'microphone' },
      });
      toast.error('Accès au microphone refusé');
      await endCall('failed');
      throw e;
    }
    roomRef.current = session;
    if (session?.client?.remoteUsers?.length > 0) {
      setCallStartedAt((prev) => prev || Date.now());
    }

    // Local participant tile — Agora uid is numeric, we coerce to string sid.
    const localSid = String(tok.uid);
    upsertParticipant({
      sid: localSid,
      identity: localSid,
      name: 'Vous',
      videoOn: publishVideo && !!session.camTrack,
      muted: !canPub,
      isLocal: true,
    });
    if (publishVideo && session.camTrack) {
      setVideoOn(true);
      // iter252a — Same buffering pattern for local preview. If the tile
      // ref is already mounted, play immediately; otherwise queue.
      const el = videoRefsRef.current[localSid];
      if (el) {
        try { session.camTrack.play(el, { fit: 'cover', mirror: true }); } catch { /* no-op */ }
      } else {
        pendingVideoTracksRef.current[localSid] = session.camTrack;
      }
    }

    try { await axios.post(`${API}/api/calls/${sessionId}/join`, {}, { withCredentials: true }); } catch { /* best-effort */ }
    // iter252a — `setCallStartedAt` is now set inside the
    // onRemoteUserPublished callback so the timer reflects real
    // connected-call duration (both peers present).
    setConnectionStatus('connected');
    setState('in-call');
    callLog('agora_connected', {
      call_id: sessionId, room_id: tok.channel,
      meta: { publishVideo, uid: tok.uid },
    });
  }, [endCall, upsertParticipant, removeParticipant]);

  // ───── START CALL (caller, p2p) ─────
  const startCall = useCallback(async (peer) => {
    // iter249b-hotfix — Clear any leftover banner from a previous failed
    // attempt before starting fresh; the user clicked again, they want
    // a brand-new feedback loop.
    setCallBlockedMessage(null);
    // iter249b — Guard 0 (P0): NEVER fail silently when the previous call
    // state didn't reset (refresh during call, network drop, etc.). Show a
    // visible toast with a "Reset" action that unsticks the UI so the user
    // can place a new call without reloading.
    if (state !== 'idle') {
      callLog('state_busy', { meta: { stage: 'startCall', state } });
      toast.error(
        t('call_context.appel_deja_en_cours_raccrochez_dabord') ||
        "Un appel est déjà en cours. Raccrochez ou réinitialisez avant de recommencer.",
        {
          duration: 8000,
          action: {
            label: t('call_context.reinitialiser') || 'Réinitialiser',
            onClick: () => {
              callLog('state_force_reset', { meta: { previous_state: state } });
              try { roomRef.current?.disconnect(); } catch { /* no-op */ }
              cleanup();
            },
          },
        },
      );
      return;
    }
    callLog('call_button_clicked', {
      meta: { type: peer?.type || 'audio', peer_id: peer?.user_id?.slice(-6) },
    });
    // iter274j — UET: Voice/Video Call Started.
    try {
      const uet = await import('../lib/uet');
      const isVideo = (peer?.type || 'audio') === 'video';
      if (isVideo) uet.trackVideoCallStarted({ conversationId: peer?.conv_id, source: 'chat' });
      else         uet.trackVoiceCallStarted({ conversationId: peer?.conv_id, source: 'chat' });
    } catch (_) { /* silent */ }
    // iter193 — Guard 1: require a connected signaling socket BEFORE
    // allocating a LiveKit session. Without this, the `call_invite` emit
    // below was silently dropped when the socket hadn't reconnected yet
    // (e.g. background tab, post-PWA-wakeup), leaving the caller stuck on
    // "calling..." forever with no visible error.
    //
    // iter249b-hotfix — Behaviour upgrade per user spec:
    //   1) Attempt an immediate reconnect.
    //   2) Wait up to 3 seconds for the 'connect' event.
    //   3) If it lands → proceed with the call (no friction for the user).
    //   4) If it doesn't → surface a *persistent red banner* via
    //      `setCallBlockedMessage`, not a toast that flashes by under the
    //      mobile OS chrome.
    if (!canSignal(socketRef)) {
      callLog('socket_not_connected', { meta: { stage: 'startCall' } });
      try { socketRef.current?.connect(); } catch { /* no-op */ }
      const reconnected = await new Promise((resolve) => {
        const sock = socketRef.current;
        if (!sock) { resolve(false); return; }
        const timer = setTimeout(() => {
          try { sock.off('connect', onConnect); } catch { /* no-op */ }
          resolve(false);
        }, 3000);
        const onConnect = () => { clearTimeout(timer); resolve(true); };
        try { sock.once('connect', onConnect); } catch { resolve(false); }
      });
      if (!reconnected) {
        callLog('socket_reconnect_failed', { meta: { stage: 'startCall' } });
        setCallBlockedMessage(
          t('calls.socket_disconnected') ||
          "⚠️ Connexion instable. Reconnexion en cours… Réessayez dans un instant.",
        );
        return;
      }
      callLog('socket_reconnect_succeeded', { meta: { stage: 'startCall' } });
    }
    // iter193 — Guard 2: request mic/cam permission in the user-gesture
    // handler, BEFORE any await. iOS Safari strictly requires permission
    // prompts to happen synchronously inside a click handler, otherwise
    // the prompt never appears and setMicrophoneEnabled(true) later fails
    // silently with `NotAllowedError`. We only probe — release immediately.
    callLog('permission_prompt_opened', {
      meta: { stage: 'startCall', type: peer?.type || 'audio' },
    });
    try {
      const probeConstraints = peer.type === 'video'
        ? { audio: true, video: { facingMode: 'user' } }
        : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(probeConstraints);
      stream.getTracks().forEach((track) => track.stop());
      callLog('permission_granted', {
        meta: { stage: 'startCall', type: peer?.type || 'audio' },
      });
    } catch (err) {
      const name = err?.name || '';
      callLog('permission_denied', {
        error: err,
        meta: { stage: 'startCall', type: peer?.type || 'audio' },
      });
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        // iter249b-hotfix — Persistent banner instead of a fading toast.
        setCallBlockedMessage(
          t('calls.permission_denied') ||
          (peer.type === 'video'
            ? "🎤 Accès caméra/micro refusé. Autorisez l'accès dans les paramètres."
            : "🎤 Accès micro refusé. Autorisez l'accès dans les paramètres."),
        );
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setCallBlockedMessage(
          t('calls.no_device') ||
          "🎤 Aucun micro ou caméra détecté sur cet appareil.",
        );
      } else {
        setCallBlockedMessage(
          t('calls.permission_denied') ||
          "Impossible d'accéder au micro/caméra.",
        );
      }
      return;
    }
    try {
      const mode = peer.type === 'video' ? 'video' : 'audio';
      const { data: sess } = await axios.post(`${API}/api/calls/session`,
        { mode, kind: 'p2p', callee_id: peer.user_id },
        { withCredentials: true },
      );
      const info = {
        session_id: sess.session_id,
        room_name: sess.room_name,
        call_id: sess.session_id,
        peer_id: peer.user_id,
        peer_name: peer.name,
        peer_avatar: peer.avatar,
        type: mode,
        kind: 'p2p',
        conv_id: peer.conv_id || null,   // ← captured from ChatPage for share-back
        direction: 'outgoing',
      };
      setCallInfo(info);
      setState('calling');
      // iter249e — Emit `call_invite` with an ack callback. The server
      // returns { ok, reason, sids_count } in <100ms, letting us surface
      // "📵 Indisponible" instantly instead of waiting 45s for the ring
      // timeout. Backwards compatible: if the server ignores the ack
      // (older versions), the timeout still fires.
      socketRef.current.emit('call_invite', {
        call_id: info.call_id,
        session_id: info.session_id,
        room_name: info.room_name,
        callee_id: peer.user_id,
        type: info.type,
        caller_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || '',
        caller_avatar: user?.avatar || '',
      }, (ack) => {
        // ack is null if the server didn't acknowledge (e.g. legacy build).
        if (!ack || typeof ack !== 'object') return;
        callLog('call_invite_ack', {
          call_id: info.call_id,
          meta: { ok: !!ack.ok, reason: ack.reason, sids: ack.sids_count },
        });
        // iter250c — Soft handling of `callee_offline`. The backend now
        // ALSO broadcasts via the user-scoped room as a safety net, so
        // even when the per-sid lookup returns empty the callee may still
        // pick up. We surface a non-blocking toast hint after a short
        // delay; the 45s ring timeout still ends the call if nobody
        // answers. This avoids the previous behaviour where the caller
        // was hung up instantly with no chance for the room broadcast to
        // succeed.
        if (!ack.ok && ack.reason === 'callee_offline') {
          callLog('call_invite_offline_soft', { call_id: info.call_id });
          // Show a soft hint after 4s so the user knows there might be
          // a connectivity issue, but DON'T cancel the call.
          setTimeout(() => {
            if (callInfoRef.current && callInfoRef.current.session_id === info.session_id
                && !callInfoRef.current.peer_picked_up) {
              setCallBlockedMessage(
                t('calls.callee_offline_soft') ||
                t('calls.callee_offline') ||
                "📵 L'utilisateur n'est peut-être pas disponible. Tentative en cours…",
              );
            }
          }, 4000);
        }
      });
      callLog('call_invite_sent', {
        call_id: info.call_id, room_id: info.room_name,
        meta: { type: info.type, kind: 'p2p' },
      });
      // iter193 — Guard 3: ring timeout. If the callee doesn't pick up
      // within 45s, hang up gracefully. Clears in cleanup() as well.
      if (ringTimeoutRef.current) clearTimeout(ringTimeoutRef.current);
      ringTimeoutRef.current = setTimeout(() => {
        if (callInfoRef.current && callInfoRef.current.session_id === info.session_id) {
          callLog('ring_timeout', { call_id: info.call_id });
          toast.info('Pas de réponse — appel annulé.');
          endCall('missed');
        }
      }, 45000);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      callLog('call_error', {
        error: e, meta: { stage: 'startCall_session', status },
      });
      if (status === 503) toast.info(detail || 'Appels non disponibles pour le moment.');
      else toast.error(detail || 'Impossible de démarrer l\'appel.');
      cleanup();
      setState('idle');
      setCallInfo(null);
    }
  }, [state, user, cleanup, endCall]);

  // ───── ACCEPT CALL (callee, p2p) ─────
  const acceptCall = useCallback(async () => {
    const info = callInfoRef.current;
    if (!info?.session_id) return;
    callLog('call_accepted', {
      call_id: info.call_id, room_id: info.room_name,
      meta: { type: info.type },
    });
    // iter193 — Same mic/cam permission probe inside the user-gesture
    // handler as startCall. Without it, Safari iOS silently denies the
    // prompt on "Accepter" clicks post-background.
    callLog('permission_prompt_opened', {
      call_id: info.call_id, meta: { stage: 'acceptCall', type: info.type },
    });
    try {
      const probe = info.type === 'video'
        ? { audio: true, video: { facingMode: 'user' } }
        : { audio: true };
      const s = await navigator.mediaDevices.getUserMedia(probe);
      s.getTracks().forEach((track) => track.stop());
      callLog('permission_granted', {
        call_id: info.call_id, meta: { stage: 'acceptCall' },
      });
    } catch (err) {
      const name = err?.name || '';
      callLog('permission_denied', {
        call_id: info.call_id, error: err, meta: { stage: 'acceptCall' },
      });
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        toast.error(info.type === 'video'
          ? t('call_context.acces_camera_micro_refuse')
          : t('call_context.acces_micro_refuse'));
      } else {
        toast.error("Impossible d'accéder au micro/caméra.");
      }
      // Still signal a reject so the caller UI moves on
      if (canSignal(socketRef)) {
        socketRef.current.emit('call_reject',
          { call_id: info.call_id, caller_id: info.peer_id });
      }
      cleanup();
      setState('idle');
      setCallInfo(null);
      return;
    }
    try {
      // iter252a — Optimistic UI: emit call_accept and flip to 'in-call'
      // BEFORE awaiting Agora's join. The peer's caller UI transitions
      // immediately, and our own InCallScreen shows with a "connecting"
      // status until publishVideo/joinChannel actually resolves.
      try { navigator.vibrate?.(30); } catch { /* no-op */ }
      if (canSignal(socketRef)) {
        socketRef.current.emit('call_accept', {
          call_id: info.call_id, session_id: info.session_id, caller_id: info.peer_id,
        });
      }
      setConnectionStatus('connecting');
      setState('in-call');     // ← optimistic — show InCallScreen now
      await connectToRoom(info.session_id, { publishVideo: info.type === 'video' });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Impossible de rejoindre l'appel.");
      await endCall('failed');
    }
  }, [connectToRoom, endCall, cleanup]);

  // ───── START GROUP CALL (host, Sprint C) ─────
  const startGroupCall = useCallback(async ({ conv_id, title, mode = 'audio', max_participants = 12 }) => {
    if (state !== 'idle') return;
    try {
      const { data: sess } = await axios.post(`${API}/api/calls/session`,
        { mode, kind: 'group', conv_id, max_participants },
        { withCredentials: true },
      );
      const info = {
        session_id: sess.session_id,
        room_name: sess.room_name,
        call_id: sess.session_id,
        peer_id: null,
        peer_name: title || 'Appel de groupe',
        peer_avatar: '',
        type: mode,
        direction: 'outgoing',
        kind: 'group',
        conv_id,
        title,
      };
      setCallInfo(info);
      // Connect to the LiveKit room FIRST. Only announce to the conv once the
      // host's media is actually live — this prevents phantom "Rejoindre"
      // banners if the host's handshake fails (permissions, network…).
      await connectToRoom(sess.session_id, { publishVideo: mode === 'video', group: true });
      if (socketRef.current?.connected) {
        socketRef.current.emit('group_call_announce', {
          conv_id, session_id: sess.session_id, mode, title,
          host_name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || '',
        });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 503) toast.info(detail || 'Appels non disponibles pour le moment.');
      else toast.error(detail || 'Impossible de démarrer l\'appel de groupe.');
      cleanup(); setState('idle'); setCallInfo(null);
    }
  }, [state, connectToRoom, cleanup, user]);

  // ───── JOIN A LIVE GROUP CALL (peer, Sprint C) ─────
  const joinGroupCall = useCallback(async ({ conv_id, session_id, mode = 'audio', title }) => {
    if (state !== 'idle') return;
    try {
      const info = {
        session_id,
        room_name: `japap_${session_id}`,
        call_id: session_id,
        peer_id: null,
        peer_name: title || 'Appel de groupe',
        peer_avatar: '',
        type: mode,
        direction: 'incoming',
        kind: 'group',
        conv_id,
        title,
      };
      setCallInfo(info);
      await connectToRoom(session_id, { publishVideo: mode === 'video', group: true });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 410) toast.info('Cet appel est déjà terminé.');
      else if (status === 403) toast.error('Vous n\'êtes pas membre de cette conversation.');
      else toast.error(detail || 'Impossible de rejoindre l\'appel.');
      cleanup(); setState('idle'); setCallInfo(null);
    }
  }, [state, connectToRoom, cleanup]);

  const rejectCall = useCallback(() => {
    const info = callInfoRef.current;
    if (info && canSignal(socketRef)) {
      socketRef.current.emit('call_reject', { call_id: info.call_id, caller_id: info.peer_id });
    }
    cleanup();
    setState('idle');
    setCallInfo(null);
  }, [cleanup]);

  // ───── Controls (iter251a — Agora session API) ─────
  const toggleMute = useCallback(async () => {
    const session = roomRef.current;
    if (!session?.setMicEnabled || !canPublish) return;  // listener: "Demander la parole" instead
    const next = !muted;
    try {
      await session.setMicEnabled(!next);  // enabled = !muted
      setMuted(next);
      const sid = String(session.client?.uid ?? '');
      if (sid) upsertParticipant({ sid, muted: next });
    } catch (e) { console.warn('mute toggle failed', e); }
  }, [muted, upsertParticipant, canPublish]);

  const applyRole = useCallback(async (speaker) => {
    const info = callInfoRef.current;
    const session = roomRef.current;
    if (!info?.session_id || !session?.setRole) return;
    const { data: tok } = await axios.post(`${API}/api/calls/token`, { session_id: info.session_id }, { withCredentials: true });
    await session.setRole(tok.can_publish, tok.token);
    setCanPublish(tok.can_publish);
    setMuted(!tok.can_publish);
    const sid = String(session.client?.uid ?? '');
    if (sid) upsertParticipant({ sid, muted: !tok.can_publish, videoOn: false });
    if (!tok.can_publish) setVideoOn(false);
  }, [upsertParticipant]);
  applyRoleRef.current = applyRole;

  const toggleSpeaker = useCallback(() => {
    const next = !speakerOn;
    setSpeakerOn(next);
    if (remoteAudioRef.current) remoteAudioRef.current.volume = next ? 1.0 : 0.35;
  }, [speakerOn]);

  // Sprint C — toggle local camera on/off (Agora)
  const toggleVideo = useCallback(async () => {
    const session = roomRef.current;
    if (!session?.setCameraEnabled || !canPublish) return;
    const next = !videoOn;
    try {
      await session.setCameraEnabled(next);
      setVideoOn(next);
      const sid = String(session.client?.uid ?? '');
      if (sid) upsertParticipant({ sid, videoOn: next });
    } catch (e) {
      toast.error('Accès à la caméra refusé');
    }
  }, [videoOn, upsertParticipant, canPublish]);

  // Sprint C — switch front/rear camera. Agora exposes
  // `track.setDevice(deviceId)` for camera swapping. For now, this is a
  // best-effort no-op stub: most desktops only have one camera, and on
  // mobile the browser exposes camera switching natively.
  const switchCamera = useCallback(async () => {
    const session = roomRef.current;
    if (!session?.camTrack) return;
    facingModeRef.current = facingModeRef.current === 'user' ? 'environment' : 'user';
    try {
      // Enumerate cameras and pick the next deviceId. Best-effort.
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter((d) => d.kind === 'videoinput');
      if (cams.length < 2) {
        toast.info('Aucune autre caméra détectée.');
        return;
      }
      const currentId = session.camTrack?.getTrackLabel?.();
      const next = cams.find((c) => c.label !== currentId) || cams[0];
      await session.camTrack.setDevice(next.deviceId);
    } catch (e) {
      toast.info('Impossible de changer de caméra sur cet appareil.');
    }
  }, []);

  // ───── Socket.io ringing listener ─────
  // Perf — reuses the single RealtimeContext connection (was a 2nd io()).
  useEffect(() => {
    if (!user || !sharedSocket) return;
    const offs = [];
    const sock = {
      on: (ev, fn) => { sharedSocket.on(ev, fn); offs.push(() => sharedSocket.off(ev, fn)); },
      emit: (...args) => sharedSocket.emit(...args),
    };
    socketRef.current = sharedSocket;
    setSocketReady(sharedSocket.connected);
    // Server auto-authenticates via httpOnly cookie on connect (see server.py).
    // Legacy explicit authenticate kept as fallback for edge cases.
    sock.on('connect', () => {
      const token = document.cookie.split(';').find(c => c.trim().startsWith('access_token='))?.split('=')[1];
      if (token) sock.emit('authenticate', { token });
      // iter249b — Reflect readiness in the React state so call buttons
      // un-grey themselves as soon as we're back online.
      setSocketReady(true);
      callLog('signaling_socket_connected', { meta: { stage: 'socket' } });
      resyncCallState();
    });
    // After a reconnection, signals sent while we were away (cancel/end) are lost:
    // ask the server whether the current call session is still alive.
    const resyncCallState = async () => {
      const info = callInfoRef.current;
      if (!info?.session_id) return;
      try {
        const { data } = await axios.get(`${API}/api/calls/${info.session_id}/state`, { withCredentials: true });
        if (data?.status !== 'ended' || callInfoRef.current?.session_id !== info.session_id) return;
        callLog('call_resync_ended', { call_id: info.call_id, meta: { stage: 'reconnect' } });
        if (info.direction === 'incoming' && !roomRef.current) {
          cleanup(); setState('idle'); setCallInfo(null); callInfoRef.current = null;
        } else {
          endCall('ended');
        }
      } catch (e) {
        // 404 = session purged → treat as ended; other errors keep the current state.
        if (e?.response?.status === 404 && callInfoRef.current?.session_id === info.session_id) {
          cleanup(); setState('idle'); setCallInfo(null); callInfoRef.current = null;
        }
      }
    };
    sock.on('jp_resync', resyncCallState);
    sock.on('disconnect', () => {
      setSocketReady(false);
      callLog('signaling_socket_disconnected', { meta: { stage: 'socket' } });
    });
    sock.on('connect_error', (e) => {
      setSocketReady(false);
      callLog('signaling_socket_connect_error', {
        meta: { stage: 'socket', message: String(e?.message || e || 'unknown') },
      });
    });
    sock.on('call_incoming', (d) => {
      // iter250c — Replace the silent `return` with an explicit cleanup
      // of any residual `callInfo` (refresh during call, network drop,
      // missed call_ended). Previously the overlay never appeared when
      // a stale callInfo lingered, leaving the callee unable to receive
      // any new incoming call until reload.
      // Also de-dupe by call_id: the backend now emits twice (per-sid +
      // user room broadcast as a safety net), so we just ignore the
      // second copy.
      const incomingCallId = d?.call_id;
      const current = callInfoRef.current;
      if (current && current.call_id === incomingCallId) {
        // Duplicate from iter250c safety-net broadcast — ignore quietly.
        return;
      }
      if (current) {
        callLog('call_incoming_residual_cleanup', {
          meta: {
            stale_call_id: String(current.call_id || '').slice(-6),
            new_call_id: String(incomingCallId || '').slice(-6),
          },
        });
        // iter251a — Drop the stale state (Agora session or LiveKit room).
        try {
          if (roomRef.current?.leave) roomRef.current.leave().catch(() => {});
          else if (roomRef.current?.disconnect) roomRef.current.disconnect();
        } catch { /* no-op */ }
        cleanup();
        setState('idle');
        setCallInfo(null);
        callInfoRef.current = null;
      }
      callLog('call_incoming_received', {
        call_id: d.call_id, room_id: d.room_name,
        meta: { type: d.type || 'audio', from: String(d.caller_id || '').slice(-6) },
      });
      setCallInfo({
        session_id: d.session_id,
        room_name: d.room_name,
        call_id: d.call_id,
        peer_id: d.caller_id,
        peer_name: d.caller_name || 'Inconnu',
        peer_avatar: d.caller_avatar || '',
        type: d.type || 'audio',
        kind: 'p2p',
        direction: 'incoming',
      });
      setState('ringing');
      // iter254b — Continuous vibration loop while ringing. Auto-stops
      // after 45s OR on accept/reject/end (see vibrateStop calls below).
      try { startVibrateLoop(); } catch { /* no-op */ }
    });
    sock.on('call_unavailable', () => {
      callLog('call_error', { meta: { reason: 'call_unavailable' } });
      // iter249b-hotfix — Persistent banner instead of a fading toast.
      setCallBlockedMessage(
        t('calls.callee_offline') ||
        "📵 Cet utilisateur n'est pas disponible pour le moment.",
      );
      endCall('failed');
    });
    sock.on('call_accepted', async (d) => {
      const info = callInfoRef.current;
      if (!info) return;
      // iter254b — caller-side: callee picked up, stop any feedback loops.
      stopVibrateLoop();
      // iter193 — clear the ring timeout as soon as the callee picks up.
      if (ringTimeoutRef.current) {
        clearTimeout(ringTimeoutRef.current);
        ringTimeoutRef.current = null;
      }
      try {
        await connectToRoom(d.session_id || info.session_id, { publishVideo: info.type === 'video' });
      } catch (e) {
        callLog('livekit_failed', {
          call_id: info.call_id, room_id: info.room_name,
          error: e, meta: { stage: 'call_accepted_connect' },
        });
        toast.error(e?.response?.data?.detail || 'Connexion à l\'appel impossible.');
        await endCall('failed');
      }
    });
    // Ignore a late event that belongs to a previous call (never end the current one).
    const isStale = (d) => Boolean(d?.call_id && callInfoRef.current?.call_id
      && d.call_id !== callInfoRef.current.call_id);
    sock.on('call_rejected', (d) => {
      if (isStale(d)) { callLog('call_rejected_stale_ignored', { call_id: d.call_id }); return; }
      callLog('call_rejected', { meta: { side: 'caller' } });
      toast.info('Appel refusé.');
      endCall('rejected');
    });
    sock.on('call_ended', (d) => { if (!isStale(d)) endCall('ended'); });
    sock.on('call_cancelled', (d) => { if (isStale(d)) return; cleanup(); setState('idle'); setCallInfo(null); });

    // Sprint C — group-call "live" announcements
    sock.on('group_call_live', (d) => {
      if (!d?.conv_id || !d?.session_id) return;
      setLiveGroupCalls((prev) => ({
        ...prev,
        [d.conv_id]: {
          session_id: d.session_id,
          mode: d.mode || 'audio',
          host_name: d.host_name || '',
          title: d.title || '',
          started_at: Date.now(),
        },
      }));
    });
    sock.on('group_call_ended', (d) => {
      if (!d?.conv_id) return;
      setLiveGroupCalls((prev) => {
        const next = { ...prev };
        delete next[d.conv_id];
        return next;
      });
    });
    return () => { offs.forEach((off) => { try { off(); } catch { /* no-op */ } }); };
  }, [user, sharedSocket, connectToRoom, endCall, cleanup]);

  const isHost = state === 'in-call' && callInfo?.direction === 'outgoing';
  const callSession = callInfo ? { session_id: callInfo.session_id, started_at: callStartedAt } : null;
  const showVideoGrid = state === 'in-call' && callInfo && (callInfo.type === 'video' || callInfo.kind === 'group');

  // iter249b-hotfix — Auto-dismiss the persistent red banner after 10s so
  // it doesn't pollute the UI forever. The user can still tap ✕ to close
  // it sooner. 10s = long enough to read on mobile PWA without overlap.
  useEffect(() => {
    if (!callBlockedMessage) return undefined;
    const id = setTimeout(() => setCallBlockedMessage(null), 10000);
    return () => clearTimeout(id);
  }, [callBlockedMessage]);

  // ITER286 — Provider value mémoïsée pour éviter la cascade de rerenders.
  // Auparavant, le Provider créait un NOUVEL OBJET à chaque render du parent
  // → tous les consumers (Layout, ChatPage, boutons d'appel, badges…)
  // rerender inutilement. Résultat : sensation générale d'instabilité.
  // Les 5 autres contexts (Auth, Settings, Tasks, Realtime, SmartUpsell)
  // étaient déjà correctement mémoïsés — celui-ci était le maillon faible.
  const contextValue = useMemo(() => ({
    state, callInfo, startCall, startGroupCall, joinGroupCall, acceptCall, rejectCall, endCall,
    toggleMute, toggleVideo, toggleSpeaker, toggleRecord, switchCamera,
    muted, speakerOn, videoOn, recording, participants, liveGroupCalls,
    connectionStatus, socketReady,
    callBlockedMessage, setCallBlockedMessage, canPublish,
  }), [ canPublish,
    state, callInfo, startCall, startGroupCall, joinGroupCall, acceptCall, rejectCall, endCall,
    toggleMute, toggleVideo, toggleSpeaker, toggleRecord, switchCamera,
    muted, speakerOn, videoOn, recording, participants, liveGroupCalls,
    connectionStatus, socketReady, callBlockedMessage,
  ]);

  return (
    <CallContext.Provider value={contextValue}>
      {children}
      <audio ref={remoteAudioRef} autoPlay />

      {state === 'ringing' && callInfo?.direction === 'incoming' && (
        <IncomingCallOverlay
          call={{
            caller_name: callInfo.peer_name,
            caller_avatar: callInfo.peer_avatar,
            mode: callInfo.type,
          }}
          onAccept={acceptCall}
          onDecline={rejectCall}
        />
      )}

      {/* Back = hide the call screen without hanging up (kept mounted so audio/video continue). */}
      {callMinimized && (state === 'calling' || state === 'in-call') && callInfo && (
        <CallReturnPill label={callInfo.peer_name || ''} onReturn={() => setCallMinimized(false)} />
      )}
      <div style={callMinimized ? { display: 'none' } : undefined} data-testid="call-screens">
      {state === 'calling' && callInfo?.direction === 'outgoing' && (
        <InCallScreen
          onMinimize={() => setCallMinimized(true)}
          session={{ session_id: callInfo.session_id, started_at: null }}
          peerName={callInfo.peer_name}
          peerAvatar={callInfo.peer_avatar}
          muted={false}
          speakerOn={speakerOn}
          recording={false}
          canRecord={false}
          callType={callInfo.type}
          connectionStatus="connecting"
          onToggleMute={() => {}}
          onToggleSpeaker={toggleSpeaker}
          onHangup={() => endCall('cancelled')}
        />
      )}

      {/* Video + Group calls use VideoCallGrid */}
      {showVideoGrid && (
        <VideoCallGrid
          session={callSession}
          participants={participants}
          videoRefs={videoRefsRef.current}
          muted={muted}
          videoOn={videoOn}
          recording={recording}
          canRecord={isHost}
          onToggleMute={toggleMute}
          onToggleVideo={toggleVideo}
          onSwitchCamera={switchCamera}
          onToggleRecord={toggleRecord}
          onHangup={() => endCall('ended')}
          onMinimize={() => setCallMinimized(true)}
        />
      )}

      {/* Audio-only 1-1 uses InCallScreen */}
      {state === 'in-call' && callInfo && !showVideoGrid && (
        <InCallScreen
          session={callSession}
          peerName={callInfo.peer_name}
          peerAvatar={callInfo.peer_avatar}
          muted={muted}
          speakerOn={speakerOn}
          recording={recording}
          canRecord={isHost}
          callType={callInfo.type}
          connectionStatus={connectionStatus}
          onToggleMute={toggleMute}
          onToggleSpeaker={toggleSpeaker}
          onHangup={() => endCall('ended')}
          onToggleRecord={toggleRecord}
          onMinimize={() => setCallMinimized(true)}
        />
      )}
      </div>

      {state === 'in-call' && callInfo?.kind === 'group' && (
        <GroupCallSeats info={callInfo} me={user} socket={sharedSocket} canPublish={canPublish} isHost={isHost} onRole={applyRole} />
      )}

      {endedSessionId && (
        <CallSummaryModal
          sessionId={endedSessionId.session_id || endedSessionId}
          convId={endedSessionId.conv_id || null}
          onClose={() => setEndedSessionId(null)}
        />
      )}
    </CallContext.Provider>
  );
}

export function useCall() {
  const ctx = useContext(CallContext);
  if (!ctx) throw new Error('useCall must be used inside CallProvider');
  return ctx;
}
